import { useMemo, useState } from 'react';
import {
  Show,
  useRecordContext,
  useGetManyReference,
  useGetMany,
  useGetOne,
  ReferenceField,
  NumberField,
  FunctionField,
  Datagrid,
  ListContextProvider,
  ResourceContextProvider,
  TextField,
  useList,
  useNotify,
  useRefresh,
} from 'react-admin';
import { User, FileText } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';
import { QualifyButton } from './QualifyButton';
import { ContactActivityFeed } from '../components/ContactActivityFeed';
import { AircallButton } from '../funerals/components/FuneralShowHelpers';
import { SmsReplyBar } from '../components/SmsReplyBar';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField as MuiTextField,
  Alert,
} from '@mui/material';

// ─── Reusable label-value row ────────────────────────────────────────────────

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between items-start py-1.5">
      <span className="text-[13px] text-muted-foreground leading-5 shrink-0">{label}</span>
      <div className="text-[13px] text-foreground leading-5 min-w-0 text-right">{children}</div>
    </div>
  );
}

// ─── Sidebar ─────────────────────────────────────────────────────────────────

function InlineSelect({
  value,
  options,
  placeholder,
  onChange,
}: {
  value: string | null;
  options: { value: string; label: string }[];
  placeholder: string;
  onChange: (val: string) => void;
}) {
  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="text-[13px] bg-transparent border border-border rounded px-1.5 py-0.5 text-right text-foreground cursor-pointer focus:outline-none focus:ring-1 focus:ring-ring"
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function CloseLostButton() {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  const { data: funeral } = useGetOne(
    'funerals',
    { id: record?.funeral_id },
    { enabled: !!record?.funeral_id },
  );

  // Hide if no lead, or if contracts are already signed
  if (!record?.lead_id) return null;
  if (record.funeral_id && funeral?.contracts_signed_at) return null;

  const handleClose = () => {
    setOpen(false);
    setReason('');
    setNotes('');
  };

  const handleConfirm = async () => {
    try {
      const updatePayload: Record<string, any> = {
          stage: 'closed_lost',
          closed_lost_reason: reason,
          closed_lost_notes: notes.trim() || null,
          updated_at: new Date().toISOString(),
        };
      if (reason === 'duplicate') {
        updatePayload.qualified_at = null;
      }
      const { error } = await supabase
        .from('leads')
        .update(updatePayload)
        .eq('id', record.lead_id);
      if (error) throw error;
      if (record.funeral_id) {
        const { error: archiveError } = await supabase
          .from('funerals')
          .update({ archived_at: new Date().toISOString() })
          .eq('id', record.funeral_id);
        if (archiveError) throw archiveError;
      }
      notify('Lead closed as lost', { type: 'success' });
      handleClose();
      refresh();
    } catch (e: any) {
      notify(e.message || 'Failed to close lead', { type: 'warning' });
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="text-[13px] text-red-600 hover:text-red-700 font-medium border border-red-300 rounded px-2.5 py-1 hover:bg-red-50 transition-colors"
      >
        Close Lost
      </button>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Close Lost — {record.contact_name || ''}</DialogTitle>
        <DialogContent>
          {record.funeral_id && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This will archive the linked funeral (#{record.funeral_id}).
            </Alert>
          )}
          <FormControl component="fieldset" sx={{ mt: 1, width: '100%' }}>
            <FormLabel component="legend">Reason *</FormLabel>
            <RadioGroup value={reason} onChange={(e) => setReason(e.target.value)}>
              <FormControlLabel value="price" control={<Radio />} label="Found lower price" />
              <FormControlLabel value="competitor" control={<Radio />} label="Went with competitor" />
              <FormControlLabel value="traditional" control={<Radio />} label="Traditional Funeral Home" />
              <FormControlLabel value="no_reply" control={<Radio />} label="No Reply" />
              <FormControlLabel value="out_of_area" control={<Radio />} label="Out of Service Area" />
              <FormControlLabel value="duplicate" control={<Radio />} label="Duplicate" />
              <FormControlLabel value="other" control={<Radio />} label="Other" />
            </RadioGroup>
          </FormControl>
          <MuiTextField
            label={reason === 'other' ? 'Notes *' : 'Notes'}
            multiline
            minRows={2}
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={!reason || (reason === 'other' && !notes.trim())}
            onClick={handleConfirm}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function QuoteShowSidebar() {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  if (!record) return null;

  const createdAt = record.created_at
    ? new Date(record.created_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : '—';

  const updateQuoteField = async (field: string, value: string) => {
    try {
      const { error } = await supabase
        .from('quotes')
        .update({ [field]: value || null })
        .eq('id', record.id);
      if (error) throw error;
      notify('Updated', { type: 'success' });
      refresh();
    } catch (e: any) {
      notify(e.message || 'Failed to update', { type: 'warning' });
    }
  };

  return (
    <div className="w-96 bg-background flex flex-col h-full min-h-0">
      <ScrollArea className="h-full">
        <div className="pb-6 pt-1">
          <Accordion
            type="multiple"
            defaultValue={['details', 'contact', 'linked_contacts']}
          >
            {/* Details section */}
            <AccordionItem value="details">
              <AccordionTrigger icon={<FileText className="w-4 h-4" />}>
                Details
              </AccordionTrigger>
              <AccordionContent>
                <Row label="Status">
                  <span className={record.qualified ? 'text-green-600 font-medium' : 'text-muted-foreground'}>
                    {record.qualified ? 'Qualified' : 'Pending'}
                  </span>
                </Row>
                <Row label="Language">{record.language?.toUpperCase() || '—'}</Row>
                <Row label="Created">{createdAt}</Row>
                {record.funeral_id && (
                  <Row label="Funeral">
                    <ReferenceField source="funeral_id" reference="funerals" link="show">
                      <NumberField source="id" />
                    </ReferenceField>
                  </Row>
                )}
                <Row label="Type">
                  <InlineSelect
                    value={record.deal_type}
                    options={[
                      { value: 'at_need', label: 'At-Need' },
                      { value: 'pre_need', label: 'Pre-Need' },
                    ]}
                    placeholder="—"
                    onChange={(val) => updateQuoteField('deal_type', val)}
                  />
                </Row>
                <Row label="Timeline">
                  <InlineSelect
                    value={record.timeline}
                    options={record.deal_type === 'pre_need' ? [
                      { value: 'Planning now', label: 'Planning now' },
                      { value: 'Follow up', label: 'Follow up' },
                    ] : [
                      { value: 'Passed', label: 'Passed' },
                      { value: 'Imminent (1-7 days)', label: 'Imminent (1-7 days)' },
                      { value: 'Near future (7-30 days)', label: 'Near future (7-30 days)' },
                      { value: 'Preplanning (30+ days)', label: 'Preplanning (30+ days)' },
                    ]}
                    placeholder="—"
                    onChange={(val) => updateQuoteField('timeline', val)}
                  />
                </Row>
                <div className="pt-2 flex items-center gap-2">
                  {!record.qualified && <QualifyButton />}
                  <CloseLostButton />
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Contact section */}
            <AccordionItem value="contact">
              <AccordionTrigger icon={<User className="w-4 h-4" />}>
                Contact
              </AccordionTrigger>
              <AccordionContent>
                <Row label="Name">{record.contact_name || '—'}</Row>
                <Row label="Phone">
                  <div className="flex items-center gap-1">
                    <span>{record.phone_number || '—'}</span>
                    {record.phone_number && <AircallButton phoneNumber={record.phone_number} />}
                  </div>
                </Row>
                <Row label="Email">{record.email || '—'}</Row>
              </AccordionContent>
            </AccordionItem>

            {/* Linked Contacts */}
            <QuoteLinkedContactsSection />
          </Accordion>
        </div>
      </ScrollArea>
    </div>
  );
}

// ─── Linked contacts accordion section ───────────────────────────────────────

function QuoteLinkedContactsSection() {
  const record = useRecordContext();

  const { data: quoteContacts, isLoading: isLoadingQC } = useGetManyReference(
    'quote_contacts',
    { target: 'quote_id', id: record?.id },
    { enabled: !!record },
  );

  const clientIds = quoteContacts?.map((qc) => qc.client_id) || [];

  const { data: contacts, isLoading: isLoadingContacts } = useGetMany(
    'clients',
    { ids: clientIds },
    { enabled: !isLoadingQC && clientIds.length > 0 },
  );

  const contactsWithRole = (contacts || []).map((c) => {
    const qc = quoteContacts?.find((qc) => qc.client_id === c.id);
    return { ...c, role: qc?.role || '' };
  });

  const listContext = useList({
    data: contactsWithRole,
    isLoading: isLoadingQC || isLoadingContacts,
  });

  if (!record) return null;
  if (!isLoadingQC && (!quoteContacts || quoteContacts.length === 0)) return null;

  return (
    <AccordionItem value="linked_contacts">
      <AccordionTrigger icon={<User className="w-4 h-4" />}>
        Linked Contacts
      </AccordionTrigger>
      <AccordionContent>
        <ResourceContextProvider value="clients">
          <ListContextProvider value={listContext}>
            <Datagrid bulkActionButtons={false} size="small">
              <FunctionField
                label="Name"
                render={(r: any) =>
                  `${r.first_name || ''} ${r.last_name || ''}`.trim() || 'N/A'
                }
              />
              <TextField source="phone_number" label="Phone" />
              <TextField source="role" label="Role" />
            </Datagrid>
          </ListContextProvider>
        </ResourceContextProvider>
      </AccordionContent>
    </AccordionItem>
  );
}

// ─── Activity feed (main content area) ───────────────────────────────────────

function QuoteActivityFeed() {
  const record = useRecordContext();

  const { data: quoteContacts, isLoading } = useGetManyReference(
    'quote_contacts',
    { target: 'quote_id', id: record?.id },
    { enabled: !!record },
  );

  const feedClientIds = useMemo(() => {
    const ids = new Set<number>();
    if (record?.client_id) ids.add(Number(record.client_id));
    if (quoteContacts) {
      for (const qc of quoteContacts) {
        if (qc.client_id) ids.add(Number(qc.client_id));
      }
    }
    return Array.from(ids);
  }, [record?.client_id, quoteContacts]);

  if (!record || isLoading) return null;

  return (
    <div className="cleo-ui flex flex-col h-full bg-background font-sans">
      <div className="px-6 py-4 border-b">
        <h1 className="text-lg font-semibold">
          {record.contact_name || 'Quote'}
        </h1>
        {record.phone_number && (
          <p className="text-sm text-muted-foreground">{record.phone_number}</p>
        )}
      </div>
      <div className="flex-1 flex flex-col min-h-0">
        <ScrollArea className="flex-1 min-h-0">
          {feedClientIds.length > 0 ? (
            <ContactActivityFeed clientIds={feedClientIds} />
          ) : (
            <div className="flex items-center justify-center h-40 text-sm text-muted-foreground">
              No activity yet
            </div>
          )}
        </ScrollArea>
      </div>
      {record.phone_number && (
        <SmsReplyBar phoneNumber={record.phone_number} contactName={record.contact_name} />
      )}
    </div>
  );
}

// ─── Main Show component ─────────────────────────────────────────────────────

export const QuoteShow = () => (
  <Show
    aside={
      <div
        className="cleo-ui"
        style={{
          height: 'calc(100vh - 128px)',
          borderRadius: '8px',
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
          border: '1px solid rgba(0,0,0,0.08)',
          overflow: 'hidden',
        }}
      >
        <QuoteShowSidebar />
      </div>
    }
    sx={{
      '& .RaShow-main': {
        maxWidth: '100%',
        display: 'flex',
        gap: '16px',
        padding: '16px 16px 16px 0',
      },
      '& .RaShow-card': {
        boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
        borderRadius: '8px',
        overflow: 'hidden',
        flex: 1,
        border: '1px solid rgba(0,0,0,0.08)',
        height: 'calc(100vh - 128px)',
        display: 'flex',
        flexDirection: 'column',
      },
      '& .RaShow-card > .MuiCardContent-root': {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        padding: 0,
        '&:last-child': { paddingBottom: 0 },
      },
      '& .RaShow-aside': {
        position: 'sticky',
        top: 16,
        alignSelf: 'flex-start',
      },
    }}
  >
    <QuoteActivityFeed />
  </Show>
);
